import { useMemo } from 'react';
import { Card, Button } from '../../../shared/ui';
import { useProjectAnalysisSummary } from '../../../shared/api';
import {
    TrendingUp,
    PieChart,
    Zap,
    Bug,
    ShieldAlert,
    Code2,
    Clock,
    Divide,
    DollarSign,
    FileCode
} from 'lucide-react';
import {
    QualityTrendChart,
    IssueDistributionChart,
    HealthRadarChart
} from '../index';

import { useAnalysisStore } from '../../../shared/lib/store/analysisStore';

interface AdvancedAnalysisProps {
    projectId: string;
    fileFilter?: string | null;
    onClearFilter?: () => void;
}

export function AdvancedAnalysis({ projectId, fileFilter, onClearFilter }: AdvancedAnalysisProps) {
    const { data: summary, isLoading } = useProjectAnalysisSummary(projectId);
    const localData = useAnalysisStore(state => state.projectData[projectId]);

    const localStats = useMemo(() => {
        if (!localData) return { totalIssues: 0, critical: 0, high: 0, medium: 0, low: 0, healthScore: 0, coverage: 0 };

        const results = fileFilter
            ? localData.results.filter(r => r.fileName === fileFilter)
            : localData.results;

        const issues = fileFilter
            ? localData.aggregatedIssues.filter(i => i.file_path === fileFilter || i.file_path === 'snippet.ts') // fallback for snippets
            : localData.aggregatedIssues;

        const critical = issues.filter(i => i.severity === 'critical').length;
        const high = issues.filter(i => i.severity === 'high').length;
        const medium = issues.filter(i => i.severity === 'medium').length;
        const low = issues.filter(i => i.severity === 'low').length;

        const penalty = (critical * 15) + (high * 10) + (medium * 5) + (low * 1);
        const healthScore = Math.max(0, Math.min(100, 100 - (penalty / Math.max(1, results.length))));

        const lines = results.reduce((acc, curr) => acc + (curr.metrics.lines || 0), 0);

        return {
            totalIssues: issues.length,
            critical, high, medium, low,
            healthScore,
            coverage: summary?.coverage ?? 0,
            lines
        };
    }, [localData, fileFilter, summary]);

    // Unified stats (Merge Backend + Local)
    const stats = useMemo(() => {
        return {
            critical: localStats.critical || (summary?.issues_by_severity?.critical || 0),
            high: localStats.high || (summary?.issues_by_severity?.high || 0),
            medium: localStats.medium || (summary?.issues_by_severity?.medium || 0),
            low: localStats.low || (summary?.issues_by_severity?.low || 0),
            total: localStats.totalIssues || (summary?.total_issues_found || 0),
            health: localStats.healthScore || (summary?.health_score || 0),
            coverage: localStats.coverage || (summary?.coverage || 0),
            lines: localStats.lines || 0
        };
    }, [localStats, summary]);

    // Derived Tech Debt metrics using unified stats
    const techDebtMetrics = useMemo(() => {
        const { critical, high, medium, low } = stats;

        // Realistic AI-Assisted Formula: Critical: 1.5h (90 mins), High: 0.75h (45 mins), Medium: 0.25h (15 mins), Low: 0.033h (2 mins)
        const hours = (critical * 1.5) + (high * 0.75) + (medium * 0.25) + (low * 0.033);

        const debtTime = hours >= 24
            ? `${Math.floor(hours / 8)}d ${Math.round(hours % 8)}h`
            : hours < 1
                ? `${Math.ceil(hours * 60)}m`
                : `${hours.toFixed(1)}h`;

        const total = critical + high + medium + low;
        const ratio = total > 0
            ? Math.min((total / 500) * 100, 15).toFixed(1) + '%'
            : '0%';

        const cost = (hours * 40).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

        return { time: debtTime, ratio, cost };
    }, [stats]);

    if (isLoading) {
        return <div className="p-20 text-center font-mono text-text-muted">Loading advanced analytics...</div>;
    }

    return (
        <div className="space-y-8 py-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-text-primary font-mono">
                        {fileFilter ? `Análisis: ${fileFilter.split('/').pop()}` : 'Análisis Detallado'}
                    </h2>
                    <p className="text-sm text-text-muted font-mono mt-1">
                        {fileFilter ? `Viendo métricas específicas del archivo seleccionado` : 'Deep insights and quality metrics'}
                    </p>
                </div>
                <div className="flex gap-3">
                    {fileFilter && (
                        <Button variant="ghost" size="sm" onClick={onClearFilter} className="text-primary hover:bg-primary/10">
                            Clear Filter
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" icon={<Zap className="h-4 w-4" />}>
                        Refrescar Datos
                    </Button>
                </div>
            </div>

            {/* Technical Debt Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card padding="md" corners="asymmetric" className="relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
                        <Clock className="h-12 w-12 text-primary" />
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs text-text-muted font-mono uppercase tracking-wider">Deuda Total</span>
                        <div className="text-3xl font-bold text-text-primary font-mono">{techDebtMetrics.time}</div>
                        <p className="text-xs text-text-muted font-mono">Tiempo estimado de corrección</p>
                    </div>
                </Card>

                <Card padding="md" corners="asymmetric-inverse" className="relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
                        <Divide className="h-12 w-12 text-success" />
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs text-text-muted font-mono uppercase tracking-wider">Ratio de Deuda</span>
                        <div className="text-3xl font-bold text-success font-mono">{techDebtMetrics.ratio}</div>
                        <p className="text-xs text-text-muted font-mono">Tasa de duplicación / complejidad</p>
                    </div>
                </Card>

                <Card padding="md" corners="asymmetric" className="relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
                        <DollarSign className="h-12 w-12 text-warning" />
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs text-text-muted font-mono uppercase tracking-wider">Estimación de Costo</span>
                        <div className="text-3xl font-bold text-warning font-mono">{techDebtMetrics.cost}</div>
                        <p className="text-xs text-text-muted font-mono">Costo derivado estimado ($40/h)</p>
                    </div>
                </Card>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Trend Chart */}
                <Card padding="lg" corners="asymmetric">
                    <div className="flex items-center gap-3 mb-6">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-text-primary font-mono">Tendencias de Calidad</h3>
                    </div>
                    <div className="h-[280px] w-full">
                        <QualityTrendChart projectId={projectId} fileFilter={fileFilter} />
                    </div>
                </Card>

                {/* Distribution Chart */}
                <Card padding="lg" corners="asymmetric-inverse">
                    <div className="flex items-center gap-3 mb-6">
                        <PieChart className="h-5 w-5 text-secondary" />
                        <h3 className="font-semibold text-text-primary font-mono">Distribución de Problemas</h3>
                    </div>
                    <div className="h-[280px] w-full">
                        <IssueDistributionChart summary={summary} stats={stats} />
                    </div>
                </Card>

                {/* Radar Chart */}
                <Card padding="lg" corners="asymmetric">
                    <div className="flex items-center gap-3 mb-6">
                        <ShieldAlert className="h-5 w-5 text-error" />
                        <h3 className="font-semibold text-text-primary font-mono">Radar de Salud del Código</h3>
                    </div>
                    <div className="h-[280px] w-full">
                        <HealthRadarChart summary={summary} localStats={localStats} />
                    </div>
                </Card>

                {/* Summary Metrics */}
                <Card padding="lg" corners="asymmetric-inverse" className="bg-surface/50 border-primary/20">
                    <h3 className="font-semibold text-text-primary font-mono mb-6">Resumen de Análisis</h3>
                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-text-muted mb-1">
                                <FileCode className="h-4 w-4" />
                                <span className="text-xs font-mono uppercase">Líneas de Código</span>
                            </div>
                            <div className="text-2xl font-bold text-text-primary font-mono">{stats.lines.toLocaleString()}</div>
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-text-muted mb-1">
                                <Bug className="h-4 w-4 text-error" />
                                <span className="text-xs font-mono uppercase text-error">Errores</span>
                            </div>
                            <div className="text-2xl font-bold text-error font-mono">{stats.high}</div>
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-text-muted mb-1">
                                <Code2 className="h-4 w-4 text-warning" />
                                <span className="text-xs font-mono uppercase text-warning">Olores de Código</span>
                            </div>
                            <div className="text-2xl font-bold text-warning font-mono">{stats.medium}</div>
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-text-muted mb-1">
                                <ShieldAlert className="h-4 w-4 text-primary" />
                                <span className="text-xs font-mono uppercase text-primary">Vulnerabilidades</span>
                            </div>
                            <div className="text-2xl font-bold text-primary font-mono">{stats.critical}</div>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
